import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../data/products';
import './CheckoutScreen.css';
import { loadFingerprintIfNeeded } from '../utils/fingerprint';

const SHOW_DEVICE_SIGNALS = process.env.REACT_APP_SHOW_DEVICE_SIGNALS === 'true';

export default function CheckoutScreen() {
  const { state, subtotalCents, clear } = useCart();
  const navigate = useNavigate();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ email?: string }>({});
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [providerOrderIdState, setProviderOrderIdState] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(20);
  const [fpFlags, setFpFlags] = useState<{ isBot?: boolean; isEmulator?: boolean; isVPN?: boolean } | null>(null);
  const [fpValue, setFpValue] = useState<string | null>(null);
  const [fpEventId, setFpEventId] = useState<string | null>(null);
  const [premiumFpValue, setPremiumFpValue] = useState<string | null>(null);
  const [isLoadingSignals, setIsLoadingSignals] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);

  // Function to read latest flags from localStorage with retry
  const readWithRetry = async (key: string, maxAttempts: number = 3, delayMs: number = 100): Promise<string | null> => {
    for (let i = 0; i < maxAttempts; i++) {
      const value = window.localStorage.getItem(key);
      if (value) return value;
      await new Promise(r => setTimeout(r, delayMs));
    }
    return null;
  };
  const readLatestFlags = async () => {
    try {
      const raw = await readWithRetry('openSourceFpFlags', 3, 100);
      if (raw) {
        const parsed = JSON.parse(raw);
        const flags = parsed?.value || parsed;
        console.log('[signals] Reading flags:', flags);
        setFpFlags({
          isBot: !!flags?.isBot,
          isEmulator: !!flags?.isEmulator,
          isVPN: !!flags?.isVPN
        });
        return true;
      }
    } catch (e) {
      console.error('[signals] Error parsing flags:', e);
    }
    return false;
  };

  // Function to read latest fingerprint from localStorage
  const readLatestFp = async () => {
    try {
      const rawId = await readWithRetry('openSourceFpId', 3, 100);
      if (!rawId) return false;
      
      try {
        const parsed = JSON.parse(rawId);
        // Handle object format
        if (parsed && typeof parsed === 'object') {
          // Always use the value field, even if it's null
          console.log('[signals] Reading fingerprint from object:', parsed.value);
          console.log('[signals] Reading eventId from object:', parsed.eventId);
          setFpValue(parsed.value || null);
          setFpEventId(parsed.eventId || null);
          return true;
        }
      } catch {
        // Not JSON, treat as raw string
        console.log('[signals] Reading fingerprint as string:', rawId);
        setFpValue(rawId);
        return true;
      }
    } catch (e) {
      console.error('[signals] Error reading fingerprint:', e);
    }
    return false;
  };

  // Function to read latest premium fingerprint from localStorage
  const readLatestPremiumFp = async () => {
    try {
      const rawId = await readWithRetry('visitorId', 3, 100);
      if (!rawId) return false;
      
      try {
        const parsed = JSON.parse(rawId);
        // Handle object format
        if (parsed && typeof parsed === 'object') {
          // Always use the value field, even if it's null
          console.log('[signals] Reading premium fingerprint from object:', parsed.value);
          setPremiumFpValue(parsed.value || null);
          return true;
        }
      } catch {
        // Not JSON, treat as raw string
        console.log('[signals] Reading premium fingerprint as string:', rawId);
        setPremiumFpValue(rawId);
        return true;
      }
    } catch (e) {
      console.error('[signals] Error reading premium fingerprint:', e);
    }
    return false;
  };

  useEffect(() => {
    let mounted = true;
    const loadAndMonitor = async () => {
      setIsLoadingSignals(true);
      
      // Initial load
      await loadFingerprintIfNeeded('checkout-mount');
      // Wait a bit for localStorage to settle
      await new Promise(r => setTimeout(r, 100));
      
      // Initial read with retry
      if (mounted) {
        const [hasFlags, hasFp, hasPremiumFp] = await Promise.all([
          readLatestFlags(),
          readLatestFp(),
          readLatestPremiumFp()
        ]);
        
        // If we don't have open-source data, start polling (premium is optional)
        if (!hasFlags || !hasFp) {
          const start = Date.now();
          while (mounted && Date.now() - start < 6000) {
            const [gotFlags, gotFp] = await Promise.all([
              readLatestFlags(),
              readLatestFp()
            ]);
            if (gotFlags && gotFp) break;
            await new Promise(r => setTimeout(r, 250));
          }
        }
        
        // Try to get premium fingerprint but don't block on it
        if (!hasPremiumFp) {
          readLatestPremiumFp().catch(() => {
            console.log('[signals] Premium fingerprint not available, continuing without it');
          });
        }
        
        setIsLoadingSignals(false);
      }
    };

    void loadAndMonitor();
    return () => { mounted = false; };
  }, []);

  // Start countdown only when order is complete
  useEffect(() => {
    if (!isComplete) return;
    setCountdown(20);
    const interval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [isComplete]);

  const items = Object.values(state.items);
  const taxCents = Math.round(subtotalCents * 0.08); // 8% tax
  const totalCents = subtotalCents + taxCents;

  const waitForFpId = async (maxMs: number): Promise<string> => {
    const start = Date.now();
    const read = (): string | null => {
      try {
        const raw = window.localStorage.getItem('openSourceFpId');
        if (!raw) return null;
        try {
          const obj = JSON.parse(raw);
          if (obj && typeof obj === 'object' && typeof obj.value === 'string') {
            return obj.value;
          }
        } catch {
          // Not JSON, treat as string
        }
        // If not JSON or no value field, treat as string
        return typeof raw === 'string' ? raw : null;
      } catch {
        return null;
      }
    };

    let id: string | null = read();
    if (id) return id;
    void loadFingerprintIfNeeded('checkout-missing');
    while (!id && Date.now() - start < maxMs) {
      await new Promise((r) => setTimeout(r, 100));
      id = read();
    }
    if (id) {
      console.log('fpId', id);
      return id;
    }
    return 'NO-FP-ID';
  };

  const handleCheckout = async () => {
    // Show processing state immediately
    setIsProcessing(true);

    // Validate email
    const nextErrors: { email?: string } = {};
    if (!email.trim()) {
      nextErrors.email = 'Email is required';
      setErrors(nextErrors);
      setIsProcessing(false);
      return;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      nextErrors.email = 'Enter a valid email';
      setErrors(nextErrors);
      setIsProcessing(false);
      return;
    }
    setErrors({});

    // Check localStorage first before loading script
    const hasExistingFp = window.localStorage.getItem('openSourceFpId');
    const hasExistingFlags = window.localStorage.getItem('openSourceFpFlags');
    const hasExistingPremiumFp = window.localStorage.getItem('visitorId');

    // Only try to load if we're missing open-source data (premium is optional)
    if (!hasExistingFp || !hasExistingFlags) {
      setIsLoadingSignals(true);
      await loadFingerprintIfNeeded('checkout-submit');
      await new Promise(r => setTimeout(r, 100)); // Wait for localStorage
      await Promise.all([readLatestFlags(), readLatestFp()]);
      
      // Try to get premium fingerprint but don't block on it
      readLatestPremiumFp().catch(() => {
        console.log('[checkout] Premium fingerprint not available, continuing without it');
      });
      
      setIsLoadingSignals(false);
    } else {
      // If we have open-source data in localStorage but not in state, read them
      if (!fpValue || !fpFlags) {
        await Promise.all([readLatestFlags(), readLatestFp()]);
      }
      
      // Try to get premium fingerprint but don't block on it
      if (!premiumFpValue) {
        readLatestPremiumFp().catch(() => {
          console.log('[checkout] Premium fingerprint not available, continuing without it');
        });
      }
    }

    // Ensure fingerprint id present (wait up to 2s)
    const fpId = await waitForFpId(2000); 

    const PINCH_API_URL = process.env.REACT_APP_PINCH_API_URL || 'https://uat-api.pinch.ai/v1/order';
    const apiKey = process.env.REACT_APP_PINCH_API_KEY || (window as any).__PINCH_API_KEY || '';

    const CUSTOM_FP_FIELD_NAME = process.env.REACT_APP_CUSTOM_FP_FIELD_NAME || 'fingerprint';
    const CUSTOM_FP_PRO_FIELD_NAME = process.env.REACT_APP_CUSTOM_FP_PRO_FIELD_NAME || 'fpProVisitorId';
    const CUSTOM_BOT_FIELD_VALUE = process.env.REACT_APP_CUSTOM_BOT_FIELD_VALUE || 'bot';
    const CUSTOM_VPN_FIELD_VALUE = process.env.REACT_APP_CUSTOM_VPN_FIELD_VALUE || 'VPN';
    const CUSTOM_EMULATOR_FIELD_VALUE = process.env.REACT_APP_CUSTOM_EMULATOR_FIELD_VALUE || 'emulator';
    const CUSTOM_SOURCE_FIELD_VALUE = process.env.REACT_APP_CUSTOM_SOURCE_FIELD_VALUE || 'source';
    const CUSTOM_FP_EVENT_ID_FIELD_VALUE = process.env.REACT_APP_CUSTOM_FP_EVENT_ID_FIELD_VALUE || 'fpEventId';

    const epochTime = Date.now();
    const toAmountString = (cents: number) => (cents / 100).toFixed(2);

    const cartItems = items.map((item) => ({
      providerProductId: String(item.product.id),
      identifiers: [
        { identifierType: 'SKU', identifier: String(item.product.id) },
      ],
      title: item.product.title,
      category: 'Others',
      brand: 'PinchStore',
      weight: '0',
      height: '0.0',
      width: '0',
      color: item.product.title,
      quantity: String(item.quantity),
      unitPrice: {
        currencyCode: '840',
        amount: toAmountString(item.product.priceCents),
      },
      taxes: [
        { currencyCode: '840', amount: '0', title: '["nofraud_skip"]' },
      ],
      discounts: [],
    }));

    // Read consolidated flags from localStorage
    const parsedFlags = (() => {
      try {
        const raw = window.localStorage.getItem('openSourceFpFlags');
        if (!raw) return {} as { isBot?: boolean; isEmulator?: boolean; isVPN?: boolean };
        const o = JSON.parse(raw);
        return { isBot: !!o?.isBot, isEmulator: !!o?.isEmulator, isVPN: !!o?.isVPN };
      } catch {
        return {} as { isBot?: boolean; isEmulator?: boolean; isVPN?: boolean };
      }
    })();

    // Include signals in customData only if we have them
    const customData = {
      ...(fpValue && { [CUSTOM_FP_FIELD_NAME]: fpValue }),
      ...(premiumFpValue && { [CUSTOM_FP_PRO_FIELD_NAME]: premiumFpValue }),
      ...(fpFlags?.isBot !== undefined && { [CUSTOM_BOT_FIELD_VALUE]: fpFlags.isBot }),
      ...(fpFlags?.isEmulator !== undefined && { [CUSTOM_EMULATOR_FIELD_VALUE]: fpFlags.isEmulator }),
      ...(fpFlags?.isVPN !== undefined && { [CUSTOM_VPN_FIELD_VALUE]: fpFlags.isVPN }),
      ...(fpEventId && { [CUSTOM_FP_EVENT_ID_FIELD_VALUE]: fpEventId }),
      [CUSTOM_SOURCE_FIELD_VALUE]: 'web',
    } as Record<string, unknown>;

    const name = 'John Doe';
    const providerOrderId = `O-${epochTime}`;
    const payload = {
      intent: 'Order',
      state: 'PAID',
      providerCartId: `cart-${epochTime}`,
      providerOrderId,
      totalPrice: { currencyCode: '840', amount: toAmountString(totalCents) },
      totalTaxes: { currencyCode: '840', amount: toAmountString(taxCents) },
      totalShippingPrice: { currencyCode: '840', amount: toAmountString(0) },
      totalDiscounts: [{ amount: toAmountString(0) }],
      lineItems: cartItems,
      deviceFingerprints: fpValue ? [{ deviceIdentifier: fpValue }] : [],
      customer: {
        providerCustomerId: Math.random().toString(36).substring(2, 15),
        providerCreatedAt: Date.now(),
        firstName: name ? name.split(' ')[0] : undefined,
        lastName: name && name.includes(' ') ? name.split(' ').slice(1).join(' ') : undefined,
        name: name || undefined,
        fullName: name || undefined,
        clientIp: '1.1.1.1',
        contactInformation: [
          { contactType: 'EMail', address: email.trim() },
          {
            "contactType": "ContactAddress",
            "address": {
                "line1": "123 Main St",
                "city": "DownTown",
                "state": "CA",
                "postalCode": "12345",
                "country": "US",
                "geoCode": {
                    "longitude": "-82.6592941",
                    "latitude": "27.7050661"
                }
            },
            "addressType": "Shipping"
          },
          {
            "contactType": "ContactAddress",
            "address": {
                "line1": "123 Main St",
                "city": "DownTown",
                "state": "CA",
                "postalCode": "12345",
                "country": "US",
                "geoCode": {
                    "longitude": "-82.6592941",
                    "latitude": "27.7050661"
                }
            },
            "addressType": "Billing"
          },
        ],
      },
      merchant: { locationInfo: { name: '', address: {} } },
      customData,
      paymentInfo: [
        {
          amount: { currencyCode: '840', amount: 0 },
          instrument: 'CC',
          method: 'NotPresent',
        },
      ],
    } as any;

    try {
      setDeviceId(fpId && fpId !== 'NO-FP-ID' ? fpId : null);
      setProviderOrderIdState(providerOrderId);
      const requestBody = JSON.stringify(payload);
      const res = await fetch(PINCH_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': apiKey
        },
        body: requestBody,
      });
      // Basic debug logs to compare with curl
      // eslint-disable-next-line no-console
      console.info('[checkout] POST', PINCH_API_URL, 'status:', res.status);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        // eslint-disable-next-line no-console
        console.error('[checkout] non-OK response', res.status, text);
      }
    } catch (e) {
      // Swallow errors for demo; could add toast/logging
      // eslint-disable-next-line no-console
      console.error('[checkout] request failed', e);
    }

    setIsProcessing(false);
    setIsComplete(true);
    clear();
    
    // Navigate back to store after 20 seconds
    setTimeout(() => {
      navigate('/');
    }, 20000);
  };

  if (items.length === 0 && !isComplete) {
    navigate('/');
    return null;
  }

  

  if (isComplete) {
    return (
      <div className="checkout-container">
        <div className="checkout-success" style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
          <h1 style={{ 
            fontSize: '2.5em', 
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <span role="img" aria-label="party popper" style={{ fontSize: '1.2em' }}>🎉</span>
            Order Complete!
          </h1>
          
          <p style={{ fontSize: '1.2em', color: '#666', marginBottom: '32px' }}>
            Thank you for your purchase. Your order will be shipped shortly.
          </p>

          {email && (
            <p style={{ 
              fontSize: '1.1em', 
              color: '#444',
              marginBottom: '16px'
            }}>
              Order placed from: <strong style={{ color: '#000' }}>{email}</strong>
            </p>
          )}

          {providerOrderIdState && (
            <div style={{ 
              marginBottom: '24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <p style={{ fontSize: '1.1em', color: '#444' }}>
                Order ID: <strong style={{ color: '#000' }}>{providerOrderIdState}</strong>
              </p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(providerOrderIdState)
                    .then(() => {
                      setCopySuccess(true);
                      setTimeout(() => setCopySuccess(false), 2000);
                    })
                    .catch(console.error);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '1em',
                  border: 'none',
                  borderRadius: '6px',
                  background: copySuccess ? '#4CAF50' : '#2196F3',
                  color: '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  fontWeight: '500'
                }}
              >
                {copySuccess ? '✓ Copied!' : 'Copy Order ID'}
              </button>
            </div>
          )}

          <p style={{ 
            marginTop: '32px',
            color: '#666',
            fontSize: '0.9em'
          }}>
            Redirecting to store in {countdown} seconds...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <h1 className="checkout-title">Checkout</h1>
      
      <div className="checkout-items">
        {items.map((item) => (
          <div key={item.product.id} className="checkout-item">
            <div className="checkout-item-info">
              <h3>{item.product.title}</h3>
              <p>Qty: {item.quantity}</p>
            </div>
            <div className="checkout-item-price">
              {formatPrice(item.product.priceCents * item.quantity)}
            </div>
          </div>
        ))}
      </div>

      <div className="checkout-summary">
        <div className="summary-row">
          <span>Subtotal:</span>
          <span>{formatPrice(subtotalCents)}</span>
        </div>
        <div className="summary-row">
          <span>Tax:</span>
          <span>{formatPrice(taxCents)}</span>
        </div>
        <div className="summary-row total">
          <span>Total:</span>
          <span>{formatPrice(totalCents)}</span>
        </div>
      </div>

      {SHOW_DEVICE_SIGNALS && (
        <div className="fp-card">
          <div className="fp-card-header">Device Signals</div>
          {isLoadingSignals ? (
            <div className="fp-row" style={{ justifyContent: 'center', padding: '20px 0' }}>
              Loading signals...
            </div>
          ) : (fpFlags || fpValue !== undefined) ? (
            <>
              <div className="fp-row">
                <span className="fp-label">Fingerprint (Open Source)</span>
                <span className="fp-value">{fpValue === null ? 'null' : fpValue || ''}</span>
              </div>
              <div className="fp-row">
                <span className="fp-label">Event ID</span>
                <span className="fp-value">{fpEventId === null ? 'null' : fpEventId || ''}</span>
              </div>
              <div className="fp-row">
                <span className="fp-label">Fingerprint (Premium)</span>
                <span className="fp-value">{premiumFpValue === null ? 'null' : premiumFpValue || ''}</span>
              </div>
              <div className="fp-row">
                <span className="fp-label">Bot</span>
                <span className="fp-value">{String(!!fpFlags?.isBot)}</span>
              </div>
              <div className="fp-row">
                <span className="fp-label">Emulator</span>
                <span className="fp-value">{String(!!fpFlags?.isEmulator)}</span>
              </div>
              <div className="fp-row">
                <span className="fp-label">VPN</span>
                <span className="fp-value">{String(!!fpFlags?.isVPN)}</span>
              </div>
            </>
          ) : (
            <div className="fp-row" style={{ justifyContent: 'center', padding: '20px 0', color: '#c00' }}>
              Failed to load signals
            </div>
          )}
        </div>
      )}

      <div className="checkout-form" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontWeight: 600 }}>
            Email (required)
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{
                width: '100%',
                marginTop: 6,
                padding: '10px 12px',
                border: '1px solid #e5e5e5',
                borderRadius: 8,
              }}
            />
          </label>
          {errors.email && (
            <span style={{ color: '#c00', fontSize: 12 }}>{errors.email}</span>
          )}
        </div>
      </div>

      <button
        className={`checkout-submit-button ${isProcessing ? 'processing' : ''}`}
        onClick={handleCheckout}
        disabled={isProcessing}
      >
        {isProcessing ? 'Processing...' : 'Complete Order'}
      </button>
    </div>
  );
}
